import React, { useState } from "react";
import {
  ApplicationButton,
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../../components/application";
import resolveAudioUrl from "../../../../utils/audio/resolveAudioUrl";
import { formatExpertBandValue } from "../expert-band/expertBandPresentation";
import FixedScaleRangeBand from "../expert-band/FixedScaleRangeBand";
import ReferenceFeedbackPresentation from "../reference-feedback/ReferenceFeedbackPresentation";

const PARAMETER_LABELS = Object.freeze({
  overallSeverity: "Overall Severity",
  roughness: "Roughness",
  breathiness: "Breathiness",
  strain: "Strain",
});
export const formatScorePoints = points => Number.isInteger(points) ? String(points) : points.toFixed(1);
export const formatJudgmentPoints = points => Number(points.toFixed(2)).toString();

export default function ReleasedAssessmentReview({ review, presentation, stageTitle = "Stage 4", onBack }) {
  const voices = review?.voices || [];
  const [index, setIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("review");
  if (!voices.length) return null;
  const voice = voices[index];
  const scoreTab = review?.scoreTab;
  const scoreAvailable = scoreTab?.available === true && scoreTab.result?.availability === "available" &&
    Number.isFinite(scoreTab.result.points) && scoreTab.result.maxPoints === 16 && scoreTab.presentation?.blockId === "ST-40" &&
    typeof scoreTab.presentation.governorClarification === "string";
  const move = next => setIndex(Math.max(0, Math.min(voices.length - 1, next)));

  return <ApplicationPage ariaLabelledby="released-review-heading" className="av-released-assessment-review" variant="workspace" width="standard">
    <ApplicationViewHeader contextLabel={stageTitle} title="Review final rating" titleId="released-review-heading" />
    {scoreAvailable && <div aria-label="Stage 4 result views" className="av-app-local-navigation av-released-assessment-review__tabs" role="tablist">
      <button aria-controls="released-review-panel" aria-selected={activeTab === "review"} className="av-app-local-navigation__item" id="released-review-tab" onClick={() => setActiveTab("review")} role="tab" type="button">Review</button>
      <button aria-controls="released-score-panel" aria-selected={activeTab === "score"} className="av-app-local-navigation__item" id="released-score-tab" onClick={() => setActiveTab("score")} role="tab" type="button">Score</button>
    </div>}
    {activeTab === "review" && <div aria-labelledby={scoreAvailable ? "released-review-tab" : undefined} id="released-review-panel" role={scoreAvailable ? "tabpanel" : undefined}>
    <ApplicationSurface className="av-released-assessment-review__introduction" variant="subtle">
      <p data-copy-block="ST-38">{presentation.introduction}</p>
    </ApplicationSurface>
    <nav className="av-released-assessment-review__navigation" aria-label="Stage 4 voice review">
      <p className="av-released-assessment-review__orientation" aria-live="polite">Voice {voice.voiceNumber} of {voices.length}</p>
      <div className="av-released-assessment-review__pagination">
        <button className="av-released-assessment-review__direction" type="button" onClick={() => move(index - 1)} disabled={index === 0}>‹ Previous</button>
        <ol className="av-released-assessment-review__strip">
          {voices.map((candidate, candidateIndex) => <li key={candidate.trialId}>
            <button
              aria-current={candidateIndex === index ? "page" : undefined}
              aria-label={`Voice ${candidate.voiceNumber}`}
              className="av-released-assessment-review__voice-button"
              data-current={candidateIndex === index ? "true" : "false"}
              onClick={() => move(candidateIndex)}
              type="button"
            >
              <span>{candidate.voiceNumber}</span>
            </button>
          </li>)}
        </ol>
        <button className="av-released-assessment-review__direction" type="button" onClick={() => move(index + 1)} disabled={index === voices.length - 1}>Next ›</button>
      </div>
    </nav>
    <ApplicationSurface as="article" ariaLabelledby={`released-review-voice-${voice.voiceNumber}`} className="av-released-assessment-review__voice" elevation="raised" variant="section">
      <ApplicationSectionHeader contextLabel="Voice review" headingLevel={2} title={`Voice ${voice.voiceNumber}`} titleId={`released-review-voice-${voice.voiceNumber}`} />
      <section className="av-released-assessment-review__audio" aria-labelledby={`released-review-audio-${voice.voiceNumber}`}>
        <p className="byce-task__section-label" id={`released-review-audio-${voice.voiceNumber}`}>Target voice</p>
        <audio aria-label={`Replay Voice ${voice.voiceNumber}`} controls preload="metadata" src={resolveAudioUrl(voice.audio.contentUrl)}>Your browser does not support audio playback.</audio>
      </section>
      <ReferenceFeedbackPresentation
        ariaLabel={`Voice ${voice.voiceNumber} reference feedback`}
        className="av-released-assessment-review__parameters"
        items={Object.entries(voice.parameters).map(([parameter, evidence]) => {
          const displayScoringWindow = evidence.displayScoringWindow || {
            scoringLow: Math.max(0, evidence.scoringWindow.scoringLow),
            scoringHigh: Math.min(100, evidence.scoringWindow.scoringHigh),
          };
          const parameterLabel = PARAMETER_LABELS[parameter] || parameter;
          const accessibleDescription = `${parameterLabel}. Your rating ${formatExpertBandValue(evidence.learnerRating)}. Observed panel range ${formatExpertBandValue(evidence.observedLow)} to ${formatExpertBandValue(evidence.observedHigh)}. Full-credit scoring window within the 0 to 100 rating scale ${formatExpertBandValue(displayScoringWindow.scoringLow)} to ${formatExpertBandValue(displayScoringWindow.scoringHigh)}. ${evidence.withinScoringWindow ? "Inside" : "Outside"} the full-credit scoring window.`;
          return {
            key: parameter,
            parameter: parameterLabel,
            headingId: `released-review-${voice.voiceNumber}-${parameter}`,
            className: "av-released-assessment-review__parameter",
            content: <>
              <FixedScaleRangeBand
              ariaLabel={accessibleDescription}
              className="av-released-assessment-review__band av-governed-feedback-band--scoring-review"
              data-scoring-status={evidence.withinScoringWindow ? "inside" : "outside"}
              learnerLabel={`You · ${formatExpertBandValue(evidence.learnerRating)}`}
              learnerValue={evidence.learnerRating}
              primaryRange={{ low: displayScoringWindow.scoringLow, high: displayScoringWindow.scoringHigh,
                className: "av-governed-feedback-band__outer--scoring-window", testId: "stage4-scoring-window" }}
              secondaryRange={{ low: evidence.observedLow, high: evidence.observedHigh,
                className: "av-governed-feedback-band__core--observed-range", testId: "stage4-observed-range" }}
              rangeLabels={{ lowLabel: formatExpertBandValue(evidence.observedLow), highLabel: formatExpertBandValue(evidence.observedHigh),
                lowPosition: evidence.observedLow, highPosition: evidence.observedHigh }}
              />
              <dl className="av-released-assessment-review__metrics">
                <div><dt>Your rating</dt><dd>{formatExpertBandValue(evidence.learnerRating)}</dd></div>
                <div><dt>Observed panel range</dt><dd>{formatExpertBandValue(evidence.observedLow)}–{formatExpertBandValue(evidence.observedHigh)}</dd></div>
                <div><dt>Full-credit scoring window</dt><dd>{formatExpertBandValue(displayScoringWindow.scoringLow)}–{formatExpertBandValue(displayScoringWindow.scoringHigh)}</dd></div>
                <div><dt>Earned credit</dt><dd>{formatJudgmentPoints(evidence.earnedCredit.awardedPoints)} of {formatJudgmentPoints(evidence.earnedCredit.maxPoints)} points</dd></div>
              </dl>
              <p className="av-released-assessment-review__range-status">
                {evidence.withinScoringWindow ? "Full credit" : "Outside the full-credit scoring window"}
              </p>
            </>,
          };
        })}
      />
    </ApplicationSurface>
    <ApplicationSurface className="av-released-assessment-review__closing" variant="subtle">
      <p data-copy-block="ST-41">{presentation.footer}</p>
    </ApplicationSurface>
    </div>}
    {scoreAvailable && activeTab === "score" && <ApplicationSurface ariaLabelledby="released-score-heading" className="av-released-assessment-review__score" elevation="raised" id="released-score-panel" role="tabpanel" variant="section">
      <ApplicationSectionHeader contextLabel="Stage 4 result" headingLevel={2} title="Final rating score" titleId="released-score-heading" />
      <p className="av-released-assessment-review__score-value">{formatScorePoints(scoreTab.result.points)} <span>/16</span></p>
      <p data-copy-block="ST-40">{scoreTab.presentation.text}</p>
      <p className="av-released-assessment-review__score-clarification">{scoreTab.presentation.governorClarification}</p>
    </ApplicationSurface>}
    {onBack && <div className="av-released-assessment-review__exit"><ApplicationButton onClick={onBack} variant="secondary">Back to Project</ApplicationButton></div>}
  </ApplicationPage>;
}
